import HelpPopup from '@wandb/weave/common/components/elements/HelpPopup';
import PanelError from '@wandb/weave/common/components/elements/PanelError';
import SliderInput from '@wandb/weave/common/components/elements/SliderInput';
import Input from '@wandb/weave/common/components/Input';
import * as globals from '@wandb/weave/common/css/globals.styles';
import {isNotNullOrUndefined, Struct} from '@wandb/weave/common/util/types';
import _ from 'lodash';
import memoize from 'memoize-one';
import React, {useCallback, useMemo, useState} from 'react';
import {useHistory} from 'react-router';
import {
  FlexibleWidthXYPlot,
  FlexibleXYPlot,
  Hint,
  HorizontalGridLines,
  LineSeries,
  LineSeriesPoint,
  MarkSeries,
  MarkSeriesPoint,
  VerticalGridLines,
  VerticalRectSeries,
  XAxis,
  YAxis,
} from 'react-vis';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Checkbox, Tab} from 'semantic-ui-react';
import useResizeObserver from 'use-resize-observer';

import {RunWithRunsetInfo} from '../../containers/RunsDataLoader';
import {SmoothingType} from '../../generated/graphql';
import {useGatedValue} from '../../state/hooks';
import {useSetHighlight} from '../../state/interactState/hooks';
import {CustomRunNames} from '../../state/views/customRunNames/types';
import * as GroupSelectionsActionsInternal from '../../state/views/groupSelections/actionsInternal';
import * as ViewHooks from '../../state/views/hooks';
import * as ColorUtil from '../../util/colors';
import {
  ColorPaletteOption,
  PLASMA_GRADIENT_SPARSE_HEX,
  toHex,
} from '../../util/colors';
import {makeFetchProjectFieldOptions} from '../../util/dropdownQueries';
import {findLineByLeastSquares, linearToLog, smooth} from '../../util/math';
import {PanelProps} from '../../util/panelProps';
import {formatYAxis} from '../../util/plotHelpers/axis';
import {
  getAxisStyleForFontSize,
  getPlotFontSize,
} from '../../util/plotHelpers/plotFontSize';
import {
  AXIS_TICK_ROTATE,
  getPlotMargin,
} from '../../util/plotHelpers/plotHelpers';
import {Grouping, Query} from '../../util/queryTypes';
import * as Run from '../../util/runs';
import * as RunTypes from '../../util/runTypes';
import {RunColorConfig} from '../../util/section';
import * as SM from '../../util/selectionmanager';
import * as urls from '../../util/urls';
import {LabeledOptionBlock as LabeledOption} from '../elements/LabeledOptionBlock';
import RangeInput from '../elements/RangeInput';
import {
  DEFAULT_GRADIENT,
  GradientPicker,
  gradientToRGBAValues,
} from '../GradientPicker';
import {useStablePanelId} from '../Panel/PanelIDContext';
import {
  PanelChartModal,
  PanelChartPreview,
  PanelChartSettings,
} from '../PanelCommon';
import {PanelTitle} from '../PanelTitle/PanelTitle';
import ProjectFieldSelector from '../ProjectFieldSelector';
import {SelectionBoundsMenu} from '../SelectionBoundsMenu';
import {InstrumentedCenteredWaveLoader as Loader} from '../utility/InstrumentedLoader';
import {usePanelsSharedRefsContext} from '../Views/contexts/PanelsSharedRefsContext';
import BoxSelection, {BoxSelectionDrawArea} from '../vis/BoxSelection';
import {useWorkspaceSettingsColorPalette} from '../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {defaultTitle, getTitleFromConfig, ScatterPlotConfig} from './common';
import * as S from './PanelScatterPlot.styles';

const INDEX_KEY = 'Index';
const MAX_AVERAGE_SMOOTHING_WEIGHT = 0.999;

type ScatterPlotProps = PanelProps<ScatterPlotConfig>;

type ScatterDataPoint = {
  x: RunTypes.Value;
  y: RunTypes.Value;
  z: RunTypes.Value;
  entityName?: string;
  projectName?: string;
  run?: RunWithRunsetInfo;
  runDisplayName: string;
  uniqueId: string;
  groupKeys: Grouping | undefined; // we need the grouping to get the right color
  color?: string | number | undefined; // The actual color displayed
  legend?: {
    [key: string]: string;
  };
  highlight?: boolean;
};

const computeData = memoize(
  (
    filtered: RunWithRunsetInfo[],
    xAxis?: string,
    yAxis?: string,
    zAxis?: string,
    xAxisMin?: number,
    xAxisMax?: number,
    yAxisMin?: number,
    yAxisMax?: number,
    zAxisMin?: number,
    zAxisMax?: number,
    zAxisLogScale?: boolean,
    legendKeys?: string[],
    query?: Query,
    customRunNames?: CustomRunNames
  ): {
    points: ScatterDataPoint[];
    gradientData: GradientPoint[];
    xDomain: [number, number] | null;
    yDomain: [number, number] | null;
    zRange: [number, number];
  } => {
    const points = filtered
      .map((run, idx) => {
        const runset = query != null ? Run.lookupRunset(run, query) : undefined;
        const entityName = runset?.entityName;
        const projectName = runset?.projectName;
        const grouping = runset?.grouping;
        const isGroup = grouping != null && grouping.length > 0;

        const runDisplayNameForProject = isGroup
          ? Run.groupedRunDisplayName(run, grouping)
          : run.displayName;
        const customRunNameForViewSection = Run.getCustomRunName(
          run.name,
          isGroup,
          customRunNames
        );
        const runDisplayName =
          customRunNameForViewSection ?? runDisplayNameForProject;

        const legend = Object.fromEntries(
          legendKeys?.map(key => {
            const val = getValForKey(key);
            return [key, Run.displayValue(val)];
          }) ?? []
        );

        const [xVal, yVal, zVal] = [xAxis, yAxis, zAxis].map(getValForKey);

        const point: ScatterDataPoint = {
          x: xVal,
          y: yVal,
          z: zAxis ? zVal : null,
          entityName,
          projectName,
          run,
          runDisplayName,
          uniqueId: Run.uniqueId(run, grouping ?? []),
          groupKeys: grouping,
          legend,
        };
        return point;

        function getValForKey(key = ''): RunTypes.Value {
          return getAxisValue(run, key, idx);
        }
      })
      .filter(point => point.x != null && point.y != null);

    const {domain: xDomain, pointsInRange: pointsInXRange} =
      getPointsAndDomainForAxis({
        points,
        axis: 'x',
        axisMin: xAxisMin,
        axisMax: xAxisMax,
      });
    const {domain: yDomain, pointsInRange: pointsInXYRange} =
      getPointsAndDomainForAxis({
        points: pointsInXRange,
        axis: 'y',
        axisMin: yAxisMin,
        axisMax: yAxisMax,
      });

    if (!zAxis) {
      return {
        points: pointsInXYRange,
        xDomain,
        yDomain,
        zRange: [0, 1],
        gradientData: [],
      };
    }

    const zVals = pointsInXYRange
      .filter(p => p.z != null)
      .map(p => Number(p.z));

    const pointsInXYZRange =
      zAxisMin != null || zAxisMax != null
        ? pointsInXYRange.filter(p => {
            if (zAxisMin != null && (p.z == null || Number(p.z) < zAxisMin)) {
              return false;
            }
            if (zAxisMax != null && (p.z == null || Number(p.z) > zAxisMax)) {
              return false;
            }
            return true;
          })
        : pointsInXYRange;

    const zMin = valueWithFallback(zAxisMin, _.min(zVals) ?? 0, zAxisLogScale);
    const zMax = valueWithFallback(zAxisMax, _.max(zVals) ?? 0, zAxisLogScale);
    const zRange: [number, number] = [zMin, zMax];
    const gradientData = getGradientData(zMin, zMax, zAxisLogScale);

    return {points: pointsInXYZRange, xDomain, yDomain, zRange, gradientData};

    function valueWithFallback(
      n: number | undefined,
      fallback: number,
      log?: boolean
    ): number {
      if (n == null || (log && n <= 0)) {
        return fallback;
      }
      return n;
    }
  }
);

const computeRegressionLine = memoize(
  (
    points: ScatterDataPoint[],
    xAxisMin?: number,
    xAxisMax?: number
  ): {line: LineSeriesPoint[]; r2: number} => {
    const numericPoints = points.filter(
      p => Number.isFinite(p.x) && Number.isFinite(p.y)
    );
    const pointsX = numericPoints.map(p => p.x as number);
    const pointsY = numericPoints.map(p => p.y as number);
    const ols = findLineByLeastSquares(pointsX, pointsY);

    const r2 = ols.r2;
    const m = ols.m;
    const b = ols.b;
    const minX = _.min(pointsX) || 0;
    const maxX = _.max(pointsX) || 0;
    const line = calcRegressionLine(
      m,
      b,
      xAxisMin ?? minX,
      xAxisMax ?? maxX
    ) as LineSeriesPoint[];
    return {line, r2};
  }
);

type ScatterPlotAxis = 'x' | 'y';

function getPointsAndDomainForAxis({
  points,
  axis,
  axisMin,
  axisMax,
}: {
  points: ScatterDataPoint[];
  axis: ScatterPlotAxis;
  axisMin: number | undefined;
  axisMax: number | undefined;
}): {
  pointsInRange: ScatterDataPoint[];
  domain: [number, number] | null;
} {
  return {
    pointsInRange: getPointsInMinMaxRange({points, axis, axisMin, axisMax}),
    domain: getDomain({points, axis, axisMin, axisMax}),
  };
}

function getPointsInMinMaxRange({
  points,
  axis,
  axisMin,
  axisMax,
}: {
  points: ScatterDataPoint[];
  axis: ScatterPlotAxis;
  axisMin: number | undefined;
  axisMax: number | undefined;
}): ScatterDataPoint[] {
  return points.filter(isNotUnderMin).filter(isNotAboveMax);

  function isNotUnderMin(p: ScatterDataPoint): boolean {
    if (axisMin == null) {
      return true;
    }
    return !(Number(p[axis]) < axisMin);
  }
  function isNotAboveMax(p: ScatterDataPoint): boolean {
    if (axisMax == null) {
      return true;
    }
    return !(Number(p[axis]) > axisMax);
  }
}

function getDomain({
  points,
  axis,
  axisMin,
  axisMax,
}: {
  points: ScatterDataPoint[];
  axis: ScatterPlotAxis;
  axisMin: number | undefined;
  axisMax: number | undefined;
}): [number, number] | null {
  const vals = points.map(p => Number(p[axis]));
  const min = _.min(vals);
  const max = _.max(vals);

  if (axisMin != null || axisMax != null) {
    return [axisMin ?? min ?? 0, axisMax ?? max ?? 0];
  }

  if (min == null || max == null) {
    return null;
  }
  return [min, max];
}

type GradientPoint = {
  x0: number;
  x: number;
  y0: number;
  y: number;
  fill: number;
  color: number;
};

function getGradientData(
  min: number,
  max: number,
  log = false
): GradientPoint[] {
  const gradientData: GradientPoint[] = [];
  const breaks = 50;
  const range = max - min;
  const step = range / breaks;
  for (let i = 0; i < breaks; i++) {
    const val = min + step * i;
    gradientData.push({
      x0: maybeConvertToLog(val),
      x: maybeConvertToLog(val + step),
      y0: 0,
      y: 1,
      fill: maybeConvertToLog(val),
      color: maybeConvertToLog(val),
    });
  }
  return gradientData;

  function maybeConvertToLog(n: number): number {
    return log ? linearToLog(n, min, max) : n;
  }
}

// See https://stackoverflow.com/questions/54520676/in-typescript-how-to-get-the-keys-of-an-object-type-whose-values-are-of-a-given
// This allows us to avoid having to cast as `string | undefined` when we know we only included keys with values of that type.
type KeysMatching<T, V> = {
  [K in keyof T]-?: T[K] extends V ? K : never;
}[keyof T];
type ScatterPlotConfigStringKeys = KeysMatching<
  ScatterPlotConfig,
  string | undefined
>;

const ScatterPlotGraphConfig: React.FC<ScatterPlotGraphProps> = React.memo(
  props => {
    const zAxisInUse =
      !!props.config.zAxis &&
      Run.keyStringDisplayName(props.config.zAxis) !== '';

    const fetchProjectFieldOptions = React.useMemo(
      () =>
        makeFetchProjectFieldOptions(
          props.pageQuery.entityName,
          props.pageQuery.projectName,
          {types: ['number'], extraKeys: [INDEX_KEY, ...Run.TIME_KEYS]}
        ),
      [props.pageQuery.entityName, props.pageQuery.projectName]
    );

    const dataTabAxisOptions: Array<{
      label: string;
      helpText: string;
      configKey: ScatterPlotConfigStringKeys;
    }> = [
      {
        label: `X`,
        helpText: `Variable logged with wandb.config, wandb.summary or wandb.log to use for X axis`,
        configKey: `xAxis`,
      },
      {
        label: `Y`,
        helpText: `Variable logged with wandb.config, wandb.summary or wandb.log to use for Y axis`,
        configKey: `yAxis`,
      },
      {
        label: `Color`,
        helpText: `Variable logged with wandb.config, wandb.summary or wandb.log to use for Z axis, represented in color`,
        configKey: `zAxis`,
      },
    ];

    const [dataTabAxisSelectedOptions, setDataTabAxisSelectedOptions] =
      useState<
        Partial<Record<ScatterPlotConfigStringKeys, undefined | string>>
      >(
        Object.fromEntries(
          dataTabAxisOptions.map(({configKey}) => [
            configKey,
            props.config[configKey],
          ])
        )
      );

    const dataTabRangeOptions: Array<{
      label: string;
      helpText: string;
      minKey: keyof ScatterPlotConfig;
      maxKey: keyof ScatterPlotConfig;
      logKey: keyof ScatterPlotConfig;
      disabled: boolean;
    }> = [
      {
        label: `X Axis`,
        helpText: `Minimum and maximum values for the x axis.  Disabled if x axis is not a number.`,
        minKey: `xAxisMin`,
        maxKey: `xAxisMax`,
        logKey: `xAxisLogScale`,
        disabled:
          props.config.xAxis == null || Run.isTimeKeyString(props.config.xAxis),
      },
      {
        label: `Y Axis`,
        helpText: `Minimum and maximum values for the y axis.  Disabled if y axis is not a number.`,
        minKey: `yAxisMin`,
        maxKey: `yAxisMax`,
        logKey: `yAxisLogScale`,
        disabled:
          props.config.yAxis == null || Run.isTimeKeyString(props.config.yAxis),
      },
      {
        label: `Z Axis`,
        helpText: `Minimum and maximum values for the z axis.  Disabled if z axis is not a number.`,
        minKey: `zAxisMin`,
        maxKey: `zAxisMax`,
        logKey: `zAxisLogScale`,
        disabled:
          !zAxisInUse ||
          props.config.zAxis == null ||
          Run.isTimeKeyString(props.config.zAxis),
      },
    ];

    const dataTab = (
      <Tab.Pane as="div" className="form-grid">
        {dataTabAxisOptions.map(({label, helpText, configKey}, i) => (
          <LabeledOption
            key={i}
            label={label}
            helpText={helpText}
            option={
              <S.StyledSearchableSelect
                options={fetchProjectFieldOptions}
                infiniteScroll
                value={dataTabAxisSelectedOptions[configKey]} // read from local state so that it updates optimistically
                onSelect={v => {
                  // update local state before saving to the config
                  setDataTabAxisSelectedOptions({
                    ...dataTabAxisSelectedOptions,
                    [configKey]: v,
                  });
                  props.updateConfig({
                    ...props.config,
                    [configKey]: v,
                  });
                }}
              />
            }
          />
        ))}
        {dataTabRangeOptions.map(
          ({label, helpText, minKey, maxKey, logKey, disabled}, i) => (
            <LabeledOption
              key={i}
              label={label}
              helpText={helpText}
              option={
                <RangeInput
                  disabled={disabled}
                  onMinChange={newVal => {
                    props.updateConfig({[minKey]: newVal});
                  }}
                  onMaxChange={newVal => {
                    props.updateConfig({[maxKey]: newVal});
                  }}
                  minValue={props.config[minKey] as number | undefined}
                  maxValue={props.config[maxKey] as number | undefined}
                  log
                  logValue={props.config[logKey] as boolean | undefined}
                  onLogChange={() =>
                    props.updateConfig({
                      ...props.config,
                      [logKey]: !props.config[logKey],
                    })
                  }
                />
              }
            />
          )
        )}
      </Tab.Pane>
    );

    const labelTab = (
      <Tab.Pane as="div" className="form-grid">
        <LabeledOption
          label="Title"
          option={
            <Input
              placeholder={defaultTitle(props.config)}
              value={props.config.chartTitle ?? ''}
              onChange={(e, {value}) => {
                props.updateConfig({
                  chartTitle: value,
                });
              }}
            />
          }
        />

        <br />
        <p>Legend Fields</p>
        <ProjectFieldSelector
          className="legend"
          disabled={false}
          query={props.pageQuery}
          types={['string', 'number', 'boolean']}
          defaultKeys={[
            'run:displayName',
            'run:name',
            'run:createdAt',
            'run:userName',
          ]}
          fluid
          multi
          selection
          searchByKeyAndText
          value={props.config.legendFields ?? ['run:displayName']}
          setValue={value => props.updateConfig({legendFields: value})}
        />
      </Tab.Pane>
    );

    const annotationsTabOptions: Array<{
      label: string;
      helpText: string;
      configKey: keyof ScatterPlotConfig;
    }> = [
      {
        label: `maximum`,
        helpText: `This shows a line that scans from left to right over the x axis and plots the running maximum y value.  It's especially useful when x axis is time and you want to plot your maximum metric value over time.`,
        configKey: `showMaxYAxisLine`,
      },
      {
        label: `minimum`,
        helpText: `This shows a line that scans from left to right over the x axis and plots the running minimum y value.  It's especially useful when x axis is time and you want to plot your minimum metric value over time.`,
        configKey: `showMinYAxisLine`,
      },
      {
        label: `average`,
        helpText: `This shows a line that scans from left to right over the x axis and plots the running average y value.  The smoothing parameter will control how much averaging happens.`,
        configKey: `showAvgYAxisLine`,
      },
    ];

    const annotationsTab = (
      <Tab.Pane as="div">
        <S.HeaderP>
          Options designed for plotting metrics over time and tracking the
          progress of a project.
        </S.HeaderP>
        <div className="time-plot-options">
          <S.AnnotationWrapper key={`annotation-wrapper-regressionP`}>
            <Checkbox
              label={{children: <>Plot linear regression</>}}
              checked={
                (props.config.showLinearRegression as boolean | undefined) ??
                false
              }
              onChange={(e, data) =>
                props.updateConfig({
                  ...props.config,
                  showLinearRegression: data.checked ?? false,
                })
              }
            />
          </S.AnnotationWrapper>
          {annotationsTabOptions.map(({label, helpText, configKey}, i) => (
            <S.AnnotationWrapper key={`annotation-wrapper-i`}>
              <Checkbox
                key={i}
                label={{
                  children: (
                    <>
                      Plot running {label} y values
                      <HelpPopup helpText={helpText} />
                    </>
                  ),
                }}
                checked={
                  (props.config[configKey] as boolean | undefined) ?? false
                }
                onChange={(e, data) =>
                  props.updateConfig({
                    ...props.config,
                    [configKey]: data.checked ?? false,
                  })
                }
              />
            </S.AnnotationWrapper>
          ))}

          {props.config.showAvgYAxisLine && (
            <div className="smoothing-average">
              <p>Smoothing for running average</p>
              <SliderInput
                min={0}
                max={MAX_AVERAGE_SMOOTHING_WEIGHT}
                step={0.01}
                value={
                  props.config.yAxisLineSmoothingWeight ??
                  MAX_AVERAGE_SMOOTHING_WEIGHT
                }
                debounceTime={100}
                hasInput
                onChange={value =>
                  props.updateConfig({
                    yAxisLineSmoothingWeight: value,
                  })
                }
              />
            </div>
          )}
        </div>
      </Tab.Pane>
    );

    const gradientTab = (
      <Tab.Pane as="div">
        {' '}
        <GradientPicker
          defaultGradient={
            props.config.customGradient
              ? {
                  type: 'customGradient',
                  gradient: props.config.customGradient,
                }
              : DEFAULT_GRADIENT
          }
          setGradient={newGradient => {
            props.updateConfig({customGradient: newGradient});
          }}
        />
      </Tab.Pane>
    );

    const settingsPanes = [
      {
        menuItem: 'Data',
        render: () => dataTab,
      },
      {
        menuItem: 'Labels',
        render: () => labelTab,
      },
      {
        menuItem: 'Annotations',
        render: () => annotationsTab,
      },
      ...(zAxisInUse
        ? [
            {
              menuItem: 'Gradient',
              render: () => gradientTab,
            },
          ]
        : []),
    ];

    return (
      <PanelChartModal>
        <PanelChartPreview>
          <ScatterPlotGraph {...props} loading={false} />
        </PanelChartPreview>
        <PanelChartSettings>
          <Tab
            panes={settingsPanes}
            menu={{
              secondary: true,
              pointing: true,
              className: 'chart-settings-tab-menu',
            }}
          />
        </PanelChartSettings>
      </PanelChartModal>
    );
  }
);

function getAxisValue(
  run: Run.RunWithSupportedKeys,
  axisKeyString: string,
  index: number
): RunTypes.Value {
  if (['Index', 'run:Index'].includes(axisKeyString)) {
    return index;
  }

  const axisKey = Run.keyFromString(axisKeyString);
  if (axisKey == null) {
    return null;
  }

  const axisValue = Run.getValue(run, axisKey);
  return Run.isTimeKey(axisKey)
    ? new Date(axisValue as string).getTime()
    : axisValue;
}

function formatTimestep(
  data: any,
  index: any,
  scale: any,
  tickTotal: any
): string {
  return Run.formatTimestamp(data);
}

function formatTickXAxis(axis: any) {
  return Run.isTimeKeyString(axis) ? formatTimestep : undefined;
}

function formatTickYAxis(axis: any) {
  return Run.isTimeKeyString(axis) ? formatTimestep : formatYAxis;
}

type HintEntry = {title: string; value: string};

function formatHint({config}: ScatterPlotProps) {
  return (point: ScatterDataPoint): HintEntry[] => {
    const nameEntry = {
      title: 'Run',
      value: point.runDisplayName,
    };

    const legendHintEntries = getHintEntriesForLegend(point.legend);

    const axisHintEntries = [
      {axisValue: point.x, axisKeyString: config.xAxis},
      {axisValue: point.y, axisKeyString: config.yAxis},
      {axisValue: point.z, axisKeyString: config.zAxis},
    ]
      .map(getHintEntryForAxis)
      .filter(isNotNullOrUndefined);

    return [nameEntry, ...legendHintEntries, ...axisHintEntries];
  };
}

function getHintEntriesForLegend(
  legend: Struct<string> | undefined
): HintEntry[] {
  if (legend == null) {
    return [];
  }
  return Object.entries(legend).map(([key, value]) => {
    return {
      title: Run.keyStringDisplayName(key),
      value: getLegendDisplayValue(value),
    };
  });
}

function getLegendDisplayValue(legendValue: string): string {
  const parsedAsFloat = Number.parseFloat(legendValue);
  const isNumberStr = !Number.isNaN(parsedAsFloat);
  const isFloat = isNumberStr && !Number.isInteger(parsedAsFloat);
  return isFloat ? parsedAsFloat.toPrecision(4) : legendValue;
}

function getHintEntryForAxis({
  axisValue,
  axisKeyString,
}: {
  axisValue: RunTypes.Value;
  axisKeyString: string | undefined;
}): HintEntry | null {
  if (axisValue == null) {
    return null;
  }
  if (axisKeyString == null) {
    return {title: '', value: Run.displayValue(axisValue)};
  }
  return {
    title: Run.keyStringDisplayName(axisKeyString),
    value: Run.isTimeKeyString(axisKeyString)
      ? Run.formatTimestamp(axisValue)
      : Run.displayValue(axisValue),
  };
}

// Timestamps have long axis tick labels, so we need to add margin for them
function getUpperPlotMargin(
  xAxis?: string,
  yAxis?: string,
  zAxis?: string
): {bottom: number; left: number; top: number} | undefined {
  const [xIsTime, yIsTime, zIsTime] = [xAxis, yAxis, zAxis].map(isTimeAxis);
  if (!xIsTime && !yIsTime && !zIsTime) {
    return undefined;
  }

  const margin = {bottom: 30, left: 0, top: 5};
  if (xIsTime) {
    margin.top = 5;
    margin.bottom = 55;
    margin.left = 55;
  }
  if (yIsTime) {
    margin.left = 100;
  }
  if (zIsTime) {
    margin.top = 20;
  }
  return margin;
}

function isTimeAxis(axis: string | undefined): boolean {
  if (!axis) {
    return false;
  }
  return Run.isTimeKeyString(axis);
}

// Calculate a running best or worst for the case where scatterplot is a
// metric over time.
function calcRunningYAxisLineData(
  goal: 'min' | 'max' | 'avg',
  points: ScatterDataPoint[],
  smoothingWeight?: number
): Array<{x: RunTypes.Value; y: RunTypes.Value}> {
  const runningLineData = getRunningLineData(goal, points);

  if (goal !== 'avg' || smoothingWeight == null) {
    return runningLineData;
  }

  const smoothedLine = smooth(
    runningLineData.map(p => p.y as number),
    {xValues: []},
    smoothingWeight,
    SmoothingType.Exponential
  );

  return runningLineData.map((p, i) => ({
    x: p.x,
    y: smoothedLine[i],
  }));
}

function calcRegressionLine(m: number, b: number, min: number, max: number) {
  return [
    {x: min, y: m * min + b},
    {x: max, y: m * max + b},
  ];
}

function getRunningLineData(
  goal: 'min' | 'max' | 'avg',
  points: ScatterDataPoint[]
): Array<{x: RunTypes.Value; y: RunTypes.Value}> {
  const pointsWithNullX = points.filter(p => p.x == null);
  const pointsWithNonNullX = points.filter(p => p.x != null);
  const pointsSortedByX = _.sortBy(pointsWithNonNullX, p => p.x);
  const sortedPoints = [...pointsSortedByX, ...pointsWithNullX];
  const pointsBucketedByX = bucketSortedPointsByX(sortedPoints);

  let runningVal: RunTypes.Value = null;

  return pointsBucketedByX.map(bucket => {
    runningVal = getNewRunningVal(bucket);
    return {x: bucket[0].x, y: runningVal};
  });

  function getNewRunningVal(bucket: ScatterDataPoint[]): RunTypes.Value {
    const bucketVal = getBucketVal(bucket);
    return shouldUpdateRunningVal(bucketVal) ? bucketVal : runningVal;
  }

  function getBucketVal(bucket: ScatterDataPoint[]): RunTypes.Value {
    const vals = bucket.map(p => p.y);
    switch (goal) {
      case 'min':
        return _.min(vals) ?? null;
      case 'max':
        return _.max(vals) ?? null;
      case 'avg': {
        const numVals = vals.filter(v => typeof v === `number`);
        if (numVals.length === 0) {
          return null;
        }
        return _.mean(numVals);
      }
    }
  }

  function shouldUpdateRunningVal(newVal: RunTypes.Value): boolean {
    if (newVal == null) {
      return false;
    }
    if (runningVal == null) {
      return true;
    }
    switch (goal) {
      case 'min':
        return newVal < runningVal;
      case 'max':
        return newVal > runningVal;
      case 'avg':
        return true;
    }
  }
}

function bucketSortedPointsByX(
  sortedPoints: ScatterDataPoint[]
): ScatterDataPoint[][] {
  const bucketed: ScatterDataPoint[][] = [];

  let currentBucket: ScatterDataPoint[] | null = null;
  sortedPoints.forEach(p => {
    if (currentBucket == null || currentBucket[0]?.x !== p.x) {
      currentBucket = [];
      bucketed.push(currentBucket);
    }
    currentBucket.push(p);
  });

  return bucketed;
}

function rangesFromBounds(
  {config: {xAxis, yAxis, zAxis}}: ScatterPlotProps,
  groupSelection: SM.GroupSelectionState
): {
  xSelect: SM.PanelSelectionConstraint;
  ySelect: SM.PanelSelectionConstraint;
  zSelect: SM.PanelSelectionConstraint;
} {
  // Gets the x,y and z selection ranges to display
  // as a gray rectangle over the scatterplot.
  const [xSelect, ySelect, zSelect] = [xAxis, yAxis, zAxis]
    .map(axis => (axis ? Run.keyFromString(axis) : null))
    .map(key =>
      key != null ? SM.getConstraintsForKey(groupSelection, key) : {}
    );

  return {xSelect, ySelect, zSelect};
}

type ScatterPlotGraphProps = ScatterPlotProps & {
  boxSelection: BoxSelectionDrawArea | null;
  runHighlight: string | null;
  setBoxSelection(newBoxSelection: BoxSelectionDrawArea | null): void;
  setRunHighlight(newRunHighlight: string | null): void;
};

function renderBoxSelection(
  {
    boxSelection,
    setBoxSelection,
    convertSelectionsToFilters,
  }: ScatterPlotGraphProps,
  groupSelection: SM.GroupSelectionState,
  setGroupSelection: (state: SM.GroupSelectionState) => void,
  xAxis?: string,
  yAxis?: string,
  xSelect?: SM.PanelSelectionConstraint,
  ySelect?: SM.PanelSelectionConstraint
): JSX.Element {
  if (
    boxSelection == null ||
    xAxis == null ||
    yAxis == null ||
    xSelect == null ||
    ySelect == null
  ) {
    return <></>;
  }

  const translateX = boxSelection.right + 33;
  const translateY = boxSelection.top + 5;

  return (
    <div
      className="selection-bounds-menu"
      style={{
        visibility: 'visible',
        transform: `translate(${translateX}px, ${translateY}px)`,
      }}>
      <SelectionBoundsMenu
        clearSelections={() => {
          setBoxSelection(null);
          setGroupSelection(SM.clearSelections([xAxis, yAxis], groupSelection));
        }}
        convertSelectionsToFilters={() => {
          if (convertSelectionsToFilters == null) {
            return;
          }
          setBoxSelection(null);
          convertSelectionsToFilters({
            [xAxis]: xSelect,
            [yAxis]: ySelect,
          });
        }}
      />
    </div>
  );
}

const ScatterPlotGraph: React.FC<ScatterPlotGraphProps> = React.memo(props => {
  const {xAxis, yAxis, zAxis} = props.config;
  const history = useHistory();

  const {runSetRefs} = usePanelsSharedRefsContext();
  const runSets = ViewHooks.useParts(runSetRefs);
  const groupSelections = ViewHooks.useParts(
    runSets.map(runSet => runSet.groupSelectionsRef)
  );
  const setGroupSelection = ViewHooks.useViewAction(
    runSets[0].groupSelectionsRef,
    GroupSelectionsActionsInternal.setGroupSelections
  );
  const {entityName: pageQueryEntityName, projectName: pageQueryProjectName} =
    props.pageQuery;

  const elementRef = React.useRef<HTMLDivElement>(null);
  const {height = 1} = useResizeObserver<HTMLDivElement>({
    ref: elementRef,
  });

  const {xSelect, ySelect} = rangesFromBounds(props, groupSelections[0]);

  // Filter out hidden/unselected runs
  const visibleRuns = useMemo(() => {
    return props.data.filtered.filter(run =>
      runIsSelected(groupSelections, run)
    );
  }, [props.data.filtered, groupSelections]);

  const errorMessage = useMemo(() => {
    if (!xAxis || !yAxis) {
      return 'Select metrics for the X Axis and Y Axis to visualize data in this scatter plot.';
    }
    if (visibleRuns.length === 0 && !props.data.loading) {
      return `Select runs that logged ${xAxis} and ${yAxis} to visualize data in this scatter plot.`;
    }
    return null;
  }, [xAxis, yAxis, visibleRuns.length, props.data.loading]);

  const {points, gradientData, xDomain, yDomain, zRange} = computeData(
    visibleRuns,
    xAxis,
    yAxis,
    zAxis,
    props.config.xAxisMin,
    props.config.xAxisMax,
    props.config.yAxisMin,
    props.config.yAxisMax,
    props.config.zAxisMin,
    props.config.zAxisMax,
    props.config.zAxisLogScale,
    props.config.legendFields ?? [],
    props.pageQuery,
    props.customRunNames
  );

  const xScale = props.config.xAxisLogScale ? 'log' : 'linear';
  const yScale = props.config.yAxisLogScale ? 'log' : 'linear';
  const zScale = props.config.zAxisLogScale ? 'log' : 'linear';

  const highlight = _.find(
    points,
    point => point.uniqueId === props.runHighlight
  );

  const totalRuns = props.data.totalRuns;
  const maxRuns = props.data.limit;

  const zAxisInUse = !!zAxis && Run.keyStringDisplayName(zAxis) !== '';

  const {gradientColorDomain, gradientColorRange, colorDomain, colorRange} =
    getZAxisData({config: props.config, zRange, zAxisInUse});
  const colorPaletteOption = useWorkspaceSettingsColorPalette();

  const pointsWithColor = getPointsWithColor({
    points,
    customRunColors: props.customRunColors,
    getColorForSelectedRun: zAxisInUse
      ? getColorForSelectedRunZAxisEnabled
      : getColorForSelectedRunZAxisDisabled,
    colorPaletteOption,
  });

  const fontSize = getPlotFontSize(props.config.fontSize ?? 'auto', height);
  const fontStyles = getAxisStyleForFontSize(fontSize);

  const plotMargin = getPlotMargin({
    axisKeys: {xAxis, yAxis, zAxis},
    axisDomain: {
      yAxis: yDomain ?? undefined,
      xAxis: xDomain ?? undefined,
    },
    axisType: {yAxis: yScale, xAxis: xScale},
    fontSize: getPlotFontSize(props.config.fontSize ?? 'auto', height),
    rotateXAxisTickLabels: true,
    panelHeight: height,
  });

  let regression: {line?: LineSeriesPoint[]; r2?: number} = {};

  if (props.config.showLinearRegression) {
    regression = computeRegressionLine(
      points,
      props.config.xAxisMin,
      props.config.xAxisMax
    );
  }

  const stablePanelId = useStablePanelId();

  return (
    <div className="chart" ref={elementRef}>
      <PanelTitle
        title={getTitleFromConfig(props.config)}
        fontSize={fontSize}
      />
      <div className="chart-content" data-test="chart-content">
        {props.data.loading ? (
          <Loader name="panel-scatter-plot-loader" size="huge" />
        ) : !props.data.loading && errorMessage != null ? (
          <PanelError
            panelID={stablePanelId}
            panelViewType="Scatter Plot"
            message={errorMessage}
          />
        ) : (
          <>
            {xAxis != null && yAxis != null && (
              <div className={'scatter-plot-container'}>
                {renderBoxSelection(
                  props,
                  groupSelections[0],
                  setGroupSelection,
                  xAxis,
                  yAxis,
                  xSelect,
                  ySelect
                )}

                {maxRuns && totalRuns > maxRuns && (
                  <div style={{float: 'right', marginRight: 15}}>
                    <span
                      style={{
                        ...fontStyles,
                        fontStyle: 'italic',
                      }}>
                      Showing first {maxRuns} runs{' '}
                    </span>
                  </div>
                )}
                {props.config.showLinearRegression && (
                  <div style={{float: 'right', marginRight: 15}}>
                    <span
                      style={{
                        ...fontStyles,
                        float: 'right',
                        fontStyle: 'italic',
                      }}>
                      R² {Run.prettyNumber(regression.r2)}
                    </span>
                  </div>
                )}

                {zAxis && zAxisInUse && (
                  // The chart above the graph showing the zrange
                  <div>
                    <FlexibleWidthXYPlot
                      height={Run.isTimeKeyString(zAxis) ? 70 : 55}
                      margin={getUpperPlotMargin(zAxis, undefined, zAxis)}
                      xType={zScale}
                      yDomain={[0, 1]}>
                      <XAxis
                        title={Run.keyStringDisplayName(zAxis)}
                        tickFormat={formatTickXAxis(zAxis) as any}
                        tickLabelAngle={
                          Run.isTimeKeyString(zAxis) ? -30 : undefined
                        }
                        style={fontStyles}
                      />
                      <VerticalRectSeries
                        colorDomain={gradientColorDomain}
                        colorRange={gradientColorRange}
                        fillDomain={gradientColorDomain}
                        fillRange={gradientColorRange}
                        data={gradientData}
                      />
                    </FlexibleWidthXYPlot>
                  </div>
                )}
                <div
                  style={{flexGrow: 1, height: '0px', userSelect: 'none'}}
                  data-test="scatter-plot-container">
                  <FlexibleXYPlot
                    className="scatter-plot-rv-night-aware"
                    margin={plotMargin}
                    xType={xScale}
                    yType={yScale}
                    xDomain={xDomain}
                    yDomain={yDomain}>
                    <VerticalGridLines style={{stroke: globals.MOON_500}} />
                    <HorizontalGridLines style={{stroke: globals.MOON_500}} />
                    <BoxSelection
                      xSelect={xSelect}
                      ySelect={ySelect}
                      onSelectChange={(
                        newXSelect,
                        newYSelect,
                        newBoxSelection
                      ) => {
                        if (
                          newXSelect.low == null &&
                          newXSelect.high == null &&
                          newYSelect.low == null &&
                          newYSelect.high == null
                        ) {
                          props.setBoxSelection(null);
                          setGroupSelection(
                            SM.clearSelections(
                              [xAxis, yAxis],
                              groupSelections[0]
                            )
                          );
                          return;
                        }

                        // Index is a special option the just sorts the runs in order
                        // it's not really a key - so selection wont work
                        // this behavior is a little weird right now but better than crashing.
                        const xAxisKey = xAxis.includes('Index')
                          ? null
                          : Run.keyFromString(xAxis);
                        const yAxisKey = yAxis.includes('Index')
                          ? null
                          : Run.keyFromString(yAxis);

                        const panelSelections: SM.PanelSelections = {};
                        if (xAxisKey) {
                          panelSelections[xAxis] = newXSelect;
                        }
                        if (yAxisKey) {
                          panelSelections[yAxis] = newYSelect;
                        }
                        props.setBoxSelection(newBoxSelection);
                        setGroupSelection(
                          SM.setSelectionsToBounds(
                            panelSelections,
                            groupSelections[0]
                          )
                        );
                      }}
                    />
                    <XAxis
                      title={Run.keyStringDisplayName(xAxis)}
                      style={fontStyles}
                      tickFormat={formatTickXAxis(xAxis) as any}
                      tickLabelAngle={AXIS_TICK_ROTATE}
                    />
                    <YAxis
                      title={Run.keyStringDisplayName(yAxis)}
                      tickFormat={formatTickYAxis(yAxis) as any}
                      style={fontStyles}
                    />
                    {props.config.showAvgYAxisLine && (
                      <LineSeries
                        data={
                          calcRunningYAxisLineData(
                            'avg',
                            pointsWithColor,
                            props.config.yAxisLineSmoothingWeight ??
                              MAX_AVERAGE_SMOOTHING_WEIGHT
                          ) as LineSeriesPoint[]
                        }
                      />
                    )}
                    {props.config.showMinYAxisLine && (
                      <LineSeries
                        data={
                          calcRunningYAxisLineData(
                            'min',
                            pointsWithColor
                          ) as LineSeriesPoint[]
                        }
                      />
                    )}
                    {props.config.showMaxYAxisLine && (
                      <LineSeries
                        data={
                          calcRunningYAxisLineData(
                            'max',
                            pointsWithColor
                          ) as LineSeriesPoint[]
                        }
                      />
                    )}
                    {props.config.showLinearRegression && (
                      <LineSeries data={regression.line} />
                    )}
                    <MarkSeries
                      colorDomain={colorDomain}
                      colorRange={colorRange}
                      colorType={zAxisInUse ? undefined : 'literal'}
                      data={pointsWithColor as MarkSeriesPoint[]}
                      size={4}
                      onValueMouseOver={value => {
                        props.setRunHighlight(value.uniqueId);
                        // setHighlight('run:name', value.uniqueId);
                      }}
                      onValueMouseOut={value => {
                        props.setRunHighlight(null);
                        // setHighlight('run:name', undefined);
                      }}
                      onValueClick={
                        ((value: ScatterDataPoint) => {
                          const entityName =
                            value.entityName ?? pageQueryEntityName;
                          const projectName =
                            value.projectName ?? pageQueryProjectName;
                          const name = value.run?.name;
                          if (name == null) {
                            return;
                          }
                          history.push(
                            urls.run({
                              entityName,
                              projectName,
                              name,
                            })
                          );
                        }) as any
                      }
                    />

                    {highlight && (
                      <Hint
                        value={highlight}
                        format={formatHint(props) as any}
                      />
                    )}
                  </FlexibleXYPlot>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

const PanelScatterPlot: React.FC<ScatterPlotProps> = React.memo(props => {
  const [boxSelection, setBoxSelection] = useState<BoxSelectionDrawArea | null>(
    null
  );

  const [runHighlight, setInternalRunHighlight] = useState<string | null>(null);

  const setHighlight = useSetHighlight();

  const setRunHighlight = useCallback(
    (newRunHighlight: string | null) => {
      setInternalRunHighlight(newRunHighlight ?? null);
      setHighlight('run:name', newRunHighlight ?? undefined);
    },
    [setInternalRunHighlight, setHighlight]
  );

  // we don't update props when loading
  const savedProps = useGatedValue(props, p => {
    return !props.loading;
  });

  // we pass through the props.groupSelection to make the rectangle
  // feel more responsive, it we passed saveProps.groupSelection the
  // rectangle wouldn't move until the page load
  const {runSetRefs} = usePanelsSharedRefsContext();
  const graphProps = {
    ...savedProps,
    // loading: props.loading,
    runSetRefs, // we need to always update runSetRefs to avoid crashing
    boxSelection,
    runHighlight,
    setBoxSelection,
    setRunHighlight,
  };

  if (props.configMode) {
    return (
      <div>
        <ScatterPlotGraphConfig {...graphProps} />
      </div>
    );
  }
  return <ScatterPlotGraph {...graphProps} />;
});

export default PanelScatterPlot;

function getZAxisData({
  config,
  zRange,
  zAxisInUse,
}: {
  config: ScatterPlotConfig;
  zRange: [number, number];
  zAxisInUse: boolean;
}): {
  gradientColorDomain: number[];
  gradientColorRange: string[];
  colorDomain?: number[];
  colorRange?: string[];
} {
  if (!zAxisInUse) {
    return {
      gradientColorDomain: [],
      gradientColorRange: [],
    };
  }

  const gradientColorRange =
    config.customGradient != null
      ? gradientToRGBAValues(config.customGradient, 10).map(([r, g, b]) => {
          return '#' + toHex(r) + toHex(g) + toHex(b) + 'ff';
        })
      : PLASMA_GRADIENT_SPARSE_HEX;

  const zDomainStep = (zRange[1] - zRange[0]) / (gradientColorRange.length - 1);
  const zDomain = gradientColorRange.map((v, i) => zRange[0] + i * zDomainStep);
  const gradientColorDomain = config.zAxisLogScale
    ? zDomain.map(z => linearToLog(z, zRange[0], zRange[1]))
    : zDomain;

  return {
    gradientColorDomain,
    gradientColorRange,
    colorDomain: gradientColorDomain,
    colorRange: gradientColorRange,
  };
}

function runIsSelected(
  groupSelections: SM.GroupSelectionState[],
  run: RunWithRunsetInfo
): boolean {
  const checkboxStates = groupSelections.map(groupSelection =>
    SM.getCheckedState(groupSelection, run, 1)
  );
  return _.includes(checkboxStates, 'checked');
}

function getPointsWithColor({
  points,
  customRunColors,
  getColorForSelectedRun,
  colorPaletteOption,
}: {
  points: ScatterDataPoint[];
  customRunColors: RunColorConfig | undefined;
  getColorForSelectedRun: (
    p: ScatterDataPoint,
    c: RunColorConfig | undefined,
    colorPaletteOption: ColorPaletteOption
  ) => ScatterDataPoint['color'];
  colorPaletteOption: ColorPaletteOption;
}): ScatterDataPoint[] {
  return points.map(p => {
    if (p.run == null) {
      return p;
    }
    const color = getColorForSelectedRun(
      p,
      customRunColors,
      colorPaletteOption
    );
    return {...p, color};
  });
}

function getColorForSelectedRunZAxisDisabled(
  point: ScatterDataPoint,
  customRunColors: RunColorConfig | undefined,
  colorPaletteOption: ColorUtil.ColorPaletteOption
): ScatterDataPoint['color'] {
  if (point.run == null) {
    return undefined;
  }
  return ColorUtil.runColor({
    run: point.run,
    groupKeys: point.groupKeys,
    customRunColors,
    colorPaletteOption,
  });
}

function getColorForSelectedRunZAxisEnabled(
  point: ScatterDataPoint,
  customRunColors: RunColorConfig | undefined,
  colorPaletteOption: ColorUtil.ColorPaletteOption
): ScatterDataPoint['color'] {
  if (point.z == null) {
    return undefined;
  }
  return typeof point.z === 'number' ? point.z : point.z.toString();
}
